import { onMounted, watch } from 'vue';
import { usePage } from '@inertiajs/vue3';
import { useToast } from 'primevue/usetoast';

/**
 * Composable that watches for flash messages from Laravel and shows toasts.
 *
 * SINGLE SOURCE OF TRUTH: this composable is initialized exactly once,
 * inside `Components/AppWrapper.vue`. Do NOT call it from pages or
 * components — each call registers an independent `watch` on
 * `page.props.flash`, so calling it twice emits two toasts for the same
 * action. Components that need to surface action results should rely on
 * the backend's `->with('success'|'error'|...)` flash, which this
 * composable already consumes.
 *
 * Usage in Laravel:
 *   return back()->with('success', 'Mensaje de éxito');
 *   return back()->with('error', 'Mensaje de error');
 *   return back()->with('warning', 'Mensaje de advertencia');
 *   return back()->with('info', 'Mensaje informativo');
 *   return back()->with('status', 'Mensaje de estado'); // Also treated as success
 */
export function useFlashMessages() {
    const page = usePage();
    const toast = useToast();

    // De-dupe guard: Inertia keeps the `flash` prop across a follow-up partial
    // reload (a success action whose `@refresh` fires
    // `router.reload({ only: [...] })` does NOT request `flash`, so the client
    // retains it), and the deep watcher re-fires with the same payload — which
    // would toast the same message twice for one action. Remember the last
    // payload shown and skip an identical immediate re-fire; the guard resets
    // whenever the flash clears, so a genuine repeat of the same action (a new
    // server flash after the bag emptied) still toasts.
    let lastShownKey = null;

    const flashKey = (flash) =>
        JSON.stringify([
            flash.success ?? null,
            flash.status ?? null,
            flash.error ?? null,
            flash.warning ?? null,
            flash.info ?? null,
        ]);

    const showFlashMessages = () => {
        const flash = page.props?.flash;

        if (!flash) {
            lastShownKey = null;
            return;
        }

        const hasAny = flash.success || flash.status || flash.error || flash.warning || flash.info;
        if (!hasAny) {
            // Empty bag: reset so the next occurrence of any message shows.
            lastShownKey = null;
            return;
        }

        const key = flashKey(flash);
        if (key === lastShownKey) return; // Same payload re-delivered — already shown.
        lastShownKey = key;

        // Success message
        if (flash.success) {
            toast.add({
                severity: 'success',
                summary: 'Éxito',
                detail: flash.success,
                life: 4000,
            });
        }

        // Status message (treated as success - used by Laravel's password reset, etc.)
        if (flash.status) {
            toast.add({
                severity: 'success',
                summary: 'Éxito',
                detail: flash.status,
                life: 4000,
            });
        }

        // Error message
        if (flash.error) {
            toast.add({
                severity: 'error',
                summary: 'Error',
                detail: flash.error,
                life: 6000,
            });
        }

        // Warning message
        if (flash.warning) {
            toast.add({
                severity: 'warn',
                summary: 'Advertencia',
                detail: flash.warning,
                life: 5000,
            });
        }

        // Info message
        if (flash.info) {
            toast.add({
                severity: 'info',
                summary: 'Información',
                detail: flash.info,
                life: 4000,
            });
        }
    };

    // Nothing is shown before mount: <Toast> subscribes to the toast bus in its
    // own mounted, and the bus drops a message nobody listens to. Inertia sets
    // the initial page during the first render, and Vue flushes watchers before
    // mounted hooks, so a watcher registered in setup would toast too early.
    onMounted(() => {
        showFlashMessages();

        watch(
            () => page.props?.flash,
            () => {
                showFlashMessages();
            },
            { deep: true }
        );
    });

    return {
        toast,
        showFlashMessages,
    };
}
